import { useSyncExternalStore } from "react";
import DandDAPI from "@utilities/smart_api";
import { DataTypeLoadStatus } from "./api_load_status_store";
import { DataType } from "@dndModels/Types";
import { DATA_TYPE_INFO } from "@dndModels/Constants";

const api = DandDAPI.getInstance();

// Every tracked load (regular/blocked/archived/live), for a dashboard/debug view
// of all the queues. Filter by .blocked/.archive_id/.live to split them back apart.
export function useApiLoadStatuses(): DataTypeLoadStatus[] {
  return useSyncExternalStore(api.load_status.subscribe, api.load_status.getSnapshot);
}

// A single load's status - undefined if it's never been requested.
export function useApiLoadStatus(data_type: DataType, blocked = false, archive_id = "", live = false): DataTypeLoadStatus | undefined {
  return useSyncExternalStore(
    api.load_status.subscribe,
    () => api.getLoadStatus(data_type, blocked, archive_id, live)
  );
}

// The data types plus everything they (transitively) depend on to load.
function withDependencies(data_types: DataType[]): Set<string> {
  const result = new Set<string>();
  const add = (data_type: DataType) => {
    if (result.has(data_type)) {
      return;
    }
    result.add(data_type);
    const info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
    (info?.depends_on_data_types ?? []).forEach(add);
  };
  data_types.forEach(add);
  return result;
}

// Average progress (0-1) across every non-live load of these data types and their
// dependencies - both source book scopes, so one finishing or restarting doesn't
// make a page's loading bar jump around. Loads that finished before `since`
// (usually when the page opened) are left out, but ones that finished after stay
// in at 100% so the average never rolls back. Undefined if nothing applies.
export function getDataTypeLoadProgress(statuses: DataTypeLoadStatus[], data_types: DataType[], since: number): number | undefined {
  const relevant = withDependencies(data_types);
  const loads = statuses.filter(o =>
    relevant.has(o.data_type) && !o.live &&
    !(o.state === "done" && (o.completed_at ?? 0) < since)
  );
  if (loads.length === 0) {
    return undefined;
  }
  return loads.reduce((sum, o) => sum + (o.state === "done" ? 1 : o.percentage), 0) / loads.length;
}

export function useDataTypeLoadProgress(data_types: DataType[], since: number): number | undefined {
  return useSyncExternalStore(
    api.load_status.subscribe,
    // Returns a number, so unchanged progress doesn't re-render.
    () => getDataTypeLoadProgress(api.load_status.getSnapshot(), data_types, since)
  );
}
